import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Spinner,
  addToast,
} from '@structural-climbing/ui'

export interface StructuralInvoice {
  id: string
  invoice_number: string
  period_month: string
  issued_on: string
  due_on: string | null
  amount: number
  currency: string
  status: 'unpaid' | 'paid'
  paid_on: string | null
  sent_at: string | null
}

export interface StructuralBilling {
  plan: { monthly_amount: number; currency: string } | null
  invoices: StructuralInvoice[]
}

function locale(lang: string) {
  return lang.startsWith('ro') ? 'ro-RO' : 'en-GB'
}

function money(amount: number, currency: string, lang: string) {
  try {
    return new Intl.NumberFormat(locale(lang), { style: 'currency', currency }).format(amount)
  } catch {
    return `${amount} ${currency}`
  }
}

/** 'YYYY-MM-DD' as a calendar day, never shifted by the viewer's time zone. */
function day(iso: string, lang: string) {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(locale(lang), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function month(iso: string, lang: string) {
  const [y, m] = iso.slice(0, 7).split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString(locale(lang), { month: 'long', year: 'numeric' })
}

function localToday() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * What Structural charges this gym and the invoices it has sent: the same
 * numbers the admin sees, read-only. Loaded only when the tab is opened.
 */
export function StructuralBillingSection() {
  const { t, i18n } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const [data, setData] = useState<StructuralBilling | null>(null)
  const [error, setError] = useState(false)
  const lang = i18n.language

  useEffect(() => {
    let cancelled = false
    getAccessTokenSilently()
      .then(async (token) => {
        const res = await fetch(`${apiUrl}/gym-billing`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) throw new Error()
        const body = (await res.json()) as StructuralBilling
        if (!cancelled) setData(body)
      })
      .catch(() => !cancelled && setError(true))
    return () => {
      cancelled = true
    }
  }, [apiUrl, getAccessTokenSilently])

  async function openPdf(id: string) {
    // Open the tab inside the tap, or iOS Safari blocks it as a popup.
    const tab = window.open('', '_blank')
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/gym-billing/invoices/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error()
      const url = URL.createObjectURL(await res.blob())
      if (tab) tab.location.href = url
      else window.location.assign(url)
    } catch {
      tab?.close()
      addToast({ title: t('structuralBilling.errorPdf'), color: 'danger' })
    }
  }

  const today = localToday()

  return (
    <Card>
      <CardHeader className="flex flex-col items-start gap-1">
        <h2 className="text-base font-semibold">{t('structuralBilling.title')}</h2>
        <p className="text-sm text-foreground/60">{t('structuralBilling.subtitle')}</p>
      </CardHeader>
      <CardBody className="flex flex-col gap-4">
        {error ? (
          <p className="text-sm text-danger">{t('structuralBilling.errorLoad')}</p>
        ) : !data ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="rounded-medium bg-default-100 p-3">
              <p className="text-sm text-foreground/60">{t('structuralBilling.planLabel')}</p>
              <p className="text-lg font-semibold">
                {data.plan
                  ? t('structuralBilling.perMonth', {
                      amount: money(data.plan.monthly_amount, data.plan.currency, lang),
                    })
                  : t('structuralBilling.noPlan')}
              </p>
            </div>

            {data.invoices.length === 0 ? (
              <p className="text-sm text-foreground/60">{t('structuralBilling.empty')}</p>
            ) : (
              <div className="flex flex-col gap-3">
                {data.invoices.map((inv) => {
                  const overdue =
                    inv.status === 'unpaid' && inv.due_on !== null && inv.due_on < today
                  return (
                    <div
                      key={inv.id}
                      className="flex flex-col gap-2 rounded-medium border border-default-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <p className="font-semibold break-words">
                          {t('structuralBilling.invoice', { number: inv.invoice_number })}
                        </p>
                        <p className="text-sm capitalize text-foreground/60">
                          {month(inv.period_month, lang)}
                        </p>
                        <p className="text-sm text-foreground/60">
                          {[
                            t('structuralBilling.issued', { date: day(inv.issued_on, lang) }),
                            inv.status === 'paid' && inv.paid_on
                              ? t('structuralBilling.paidOn', { date: day(inv.paid_on, lang) })
                              : inv.due_on
                                ? t('structuralBilling.due', { date: day(inv.due_on, lang) })
                                : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">
                          {money(inv.amount, inv.currency, lang)}
                        </span>
                        <Chip
                          size="sm"
                          variant="flat"
                          color={inv.status === 'paid' ? 'success' : overdue ? 'danger' : 'warning'}
                        >
                          {inv.status === 'paid'
                            ? t('structuralBilling.statusPaid')
                            : overdue
                              ? t('structuralBilling.statusOverdue')
                              : t('structuralBilling.statusUnpaid')}
                        </Chip>
                        <Button size="sm" variant="flat" onPress={() => void openPdf(inv.id)}>
                          {t('structuralBilling.viewPdf')}
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  )
}
